import type { ArticleData } from "@/mocks/articles";

export const SITE_URL: string =
  (import.meta.env.VITE_SITE_URL as string) || "https://cssbuybr.skin";

export const SITE_NAME = "CSSBuy Spreadsheet";

export const FAVICON_URL =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png";

export const LAST_MODIFIED = "2026-08-26";

export interface FaqItem {
  q: string;
  a: string;
}

export const defaultFaqs: FaqItem[] = [
  {
    q: "What is CSSBuy Spreadsheet?",
    a: "CSSBuy Spreadsheet is an organized directory of product links, categories, and research information designed to help buyers find and compare products more efficiently.",
  },
  {
    q: "How do I use CSSBuy Spreadsheet?",
    a: "Choose a category, browse the listed products, compare information, and click the product links to visit seller pages for current details.",
  },
  {
    q: "Is CSSBuy legit?",
    a: "CSSBuy is a known shopping agent platform. Always verify current information, check reviews, and use official channels for transactions.",
  },
  {
    q: "Can spreadsheet links expire?",
    a: "Yes, product links can change or expire as sellers update their listings. Always verify links before purchasing.",
  },
  {
    q: "How do I check QC photos?",
    a: "QC photos are typically available through your agent account after the item arrives at the warehouse. Review them carefully before approving shipment.",
  },
  {
    q: "Is this website affiliated with CSSBuy?",
    a: "No, cssbuybr.skin is an independent informational resource and is not affiliated with or endorsed by CSSBuy.",
  },
];

export function buildWebSiteSchema(
  siteUrl: string,
  name: string,
  description: string,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name,
    url: siteUrl,
    description,
    inLanguage: "en",
  };
}

export function buildOrganizationSchema(
  siteUrl: string,
  name: string,
  logoUrl: string,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name,
    url: siteUrl,
    logo: logoUrl,
  };
}

export function buildArticleSchema(
  article: ArticleData,
  siteUrl: string,
  publisherName: string,
  logoUrl: string,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title.en,
    description: article.excerpt.en,
    image: [article.image],
    datePublished: article.date,
    dateModified: LAST_MODIFIED,
    author: {
      "@type": "Organization",
      name: publisherName,
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: publisherName,
      logo: {
        "@type": "ImageObject",
        url: logoUrl,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/article/${article.slug}`,
    },
  };
}

export function buildFaqSchema(faqs: FaqItem[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };
}